'use client';

import * as React from 'react';
import { motion, useReducedMotion } from 'motion/react';

import { cn } from '@workspace/ui/lib/utils';

import { McpEndpointCard } from '~/components/mcp/mcp-endpoint-card';
import { MCP_CONNECT } from '~/lib/mcp-content';

/**
 * §4 How to connect. The copyable endpoint is the centerpiece, followed by the
 * three universal steps and a per-client tab strip (Claude / Cursor / Other).
 */
export function McpConnect(): React.JSX.Element {
  const shouldReduceMotion = useReducedMotion();
  const reduced = !!shouldReduceMotion;
  // Widen to the union of client ids — the as-const content object would
  // otherwise infer the literal type of clients[0].id and reject the others.
  const [activeClient, setActiveClient] = React.useState<
    (typeof MCP_CONNECT.clients)[number]['id']
  >(MCP_CONNECT.clients[0].id);

  const active =
    MCP_CONNECT.clients.find((c) => c.id === activeClient) ??
    MCP_CONNECT.clients[0];

  return (
    <section
      id="connect"
      className="relative bg-[color:var(--paper-2)] py-20 md:py-28 scroll-mt-20"
    >
      <div className="w-full mx-auto max-w-[1320px] px-6 md:px-8 lg:px-12 xl:px-16">
        <motion.div
          className="mx-auto max-w-[760px] text-center mb-10 md:mb-12"
          initial={{ opacity: 0, y: reduced ? 0 : 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
        >
          <span className="eyebrow block mb-3">{MCP_CONNECT.eyebrow}</span>
          <h2 className="display-h2 text-3xl md:text-4xl lg:text-5xl text-[color:var(--ink)] [text-wrap:balance]">
            <span className="text-[color:var(--ink-3)]">
              {MCP_CONNECT.headlineLead}
            </span>{' '}
            <span>{MCP_CONNECT.headlineRest}</span>
          </h2>
          <p className="mt-5 text-base md:text-lg text-[color:var(--ink-3)] leading-relaxed [text-wrap:balance]">
            {MCP_CONNECT.description}
          </p>
        </motion.div>

        {/* Endpoint card */}
        <motion.div
          className="mx-auto max-w-[640px]"
          initial={{ opacity: 0, y: reduced ? 0 : 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
        >
          <McpEndpointCard />
        </motion.div>

        {/* Steps */}
        <div className="mx-auto mt-12 grid max-w-[960px] grid-cols-1 gap-5 md:grid-cols-3">
          {MCP_CONNECT.steps.map((step, i) => (
            <motion.div
              key={step.number}
              className="surface-card rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--paper)] p-6"
              initial={{ opacity: 0, y: reduced ? 0 : 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-70px' }}
              transition={{ delay: reduced ? 0 : i * 0.08, duration: 0.45 }}
            >
              <span
                className="flex h-9 w-9 items-center justify-center rounded-full font-mono text-sm font-medium"
                style={{
                  background: 'var(--mint)',
                  color: 'var(--accent-darker)'
                }}
              >
                {step.number}
              </span>
              <h3 className="mt-4 text-[17px] font-semibold text-[color:var(--ink)]">
                {step.title}
              </h3>
              <p className="mt-2 text-[14px] leading-relaxed text-[color:var(--ink-3)]">
                {step.body}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Client tabs */}
        <motion.div
          className="mx-auto mt-12 max-w-[640px]"
          initial={{ opacity: 0, y: reduced ? 0 : 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
        >
          <div
            role="group"
            aria-label="MCP client setup instructions"
            className="flex flex-wrap justify-center gap-2"
          >
            {MCP_CONNECT.clients.map((client) => (
              <button
                key={client.id}
                type="button"
                aria-pressed={client.id === activeClient}
                aria-controls="mcp-client-instructions"
                onClick={() => setActiveClient(client.id)}
                className={cn(
                  'min-h-11 rounded-full border px-4 py-2 text-[13px] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-700',
                  client.id === activeClient
                    ? 'border-teal-500/40 bg-[color:var(--mint)] text-[color:var(--accent-darker)]'
                    : 'border-[color:var(--hairline)] bg-[color:var(--paper)] text-[color:var(--ink-3)] hover:text-[color:var(--ink)]'
                )}
              >
                {client.name}
              </button>
            ))}
          </div>

          <div
            id="mcp-client-instructions"
            className="surface-card mt-5 rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--paper)] p-6"
          >
            <ol className="flex flex-col gap-3">
              {active.steps.map((s, i) => (
                <li
                  key={s}
                  className="flex items-start gap-3 text-[14px] text-[color:var(--ink-2)]"
                >
                  <span className="mono-num mt-0.5 text-[12px] text-[color:var(--ink-4)]">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
